// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling — the call shape the web client works with, normalised
// from the REST (API.1) and ActionCable (EVENTS.*) payloads. The contract
// names the fields but not their nesting, so normalizeCall accepts both a
// nested (`contact: {name, thumbnail}`) and a flat (`contact_name`) shape.

// whatsapp-calling.CALL_MODEL.2
export const CALL_STATUS = Object.freeze({
  RINGING: 'ringing',
  ANSWERED: 'answered',
  COMPLETED: 'completed',
  MISSED: 'missed',
  DECLINED: 'declined',
  FAILED: 'failed',
  PERMISSION_REQUESTED: 'permission_requested',
});

export const FINAL_STATUSES = [
  CALL_STATUS.COMPLETED,
  CALL_STATUS.MISSED,
  CALL_STATUS.DECLINED,
  CALL_STATUS.FAILED,
];

export const CALL_DIRECTION = Object.freeze({
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
});

// whatsapp-calling.ROUTING.5
export const CALL_AVAILABILITY = Object.freeze({
  AVAILABLE: 'available',
  CHATS_ONLY: 'chats_only',
  FORWARD_TO_PHONE: 'forward_to_phone',
  OFFLINE: 'offline',
});

export const AVAILABILITY_OPTIONS = [
  CALL_AVAILABILITY.AVAILABLE,
  CALL_AVAILABILITY.CHATS_ONLY,
  CALL_AVAILABILITY.FORWARD_TO_PHONE,
  CALL_AVAILABILITY.OFFLINE,
];

// Status order, used so an out-of-order event never moves a call backwards
// (mirrors META.3 on the client).
const STATUS_RANK = {
  [CALL_STATUS.PERMISSION_REQUESTED]: 0,
  [CALL_STATUS.RINGING]: 1,
  [CALL_STATUS.ANSWERED]: 2,
  [CALL_STATUS.COMPLETED]: 3,
  [CALL_STATUS.MISSED]: 3,
  [CALL_STATUS.DECLINED]: 3,
  [CALL_STATUS.FAILED]: 3,
};

// whatsapp-calling.WEB.4 / CALL_MODEL.3 — the call's voice_call message carries
// kaption_call_id and has no text content (it never reaches WhatsApp or the
// assistant), so the timeline must render it on that basis, not on content.
export const isKaptionCallMessage = (contentType, contentAttributes) =>
  contentType === 'voice_call' &&
  !!(contentAttributes?.kaptionCallId || contentAttributes?.kaption_call_id);

export const isFinalStatus = status => FINAL_STATUSES.includes(status);

export const isForwardTransition = (from, to) => {
  if (!from || !to) return true;
  if (isFinalStatus(from)) return false;
  return (STATUS_RANK[to] ?? 0) >= (STATUS_RANK[from] ?? 0);
};

const pick = (...values) => values.find(v => v !== undefined && v !== null);

// whatsapp-calling.API.8 — the backend sends every timestamp as unix seconds
// (like Chatwoot's message payloads). The web client works with ISO strings,
// so numbers (and numeric strings) are converted here; ISO passes through.
export const toIsoTime = value => {
  if (value === undefined || value === null || value === '') return null;
  const isNumeric =
    typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value));
  if (!isNumeric) return String(value);
  const numeric = Number(value);
  // Seconds, unless it is already milliseconds (13 digits).
  const ms = numeric > 1e12 ? numeric : numeric * 1000;
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// whatsapp-calling-ownership — {id, name, avatar_url} → {id, name, avatarUrl}.
export const normalizeUser = user =>
  user?.id || user?.name
    ? {
        id: user.id ?? null,
        name: user.name || '',
        avatarUrl: user.avatar_url || user.avatarUrl || '',
        // whatsapp-calling-ai.UI.2 — "assistant" draws the AI chip.
        kind: user.kind || 'agent',
      }
    : null;

// whatsapp-calling-ai.UI.2, .3 — the Studio Chat AI, as a user or a leg.
export const isAssistant = who => who?.kind === 'assistant';

// whatsapp-calling-ai.SUMMARY.2 / UI.5 — the AI call summary. Read from the
// call JSON's `ai` or the call message's content_attributes.data.ai
// (camelCased in message lists). Null when there is no summary.
const SUMMARY_OUTCOMES = [
  'resolved',
  'follow_up',
  'unresolved',
  'sale',
  'complaint',
];
const SUMMARY_SENTIMENTS = ['positive', 'neutral', 'negative'];
export const normalizeAiSummary = raw => {
  if (!raw?.summary) return null;
  const outcome = SUMMARY_OUTCOMES.includes(raw.outcome) ? raw.outcome : null;
  const sentiment = SUMMARY_SENTIMENTS.includes(raw.sentiment)
    ? raw.sentiment
    : null;
  return {
    summary: raw.summary,
    outcome,
    intent: raw.intent || null,
    sentiment,
    followUpDraft: raw.follow_up_draft ?? raw.followUpDraft ?? null,
    labels: Array.isArray(raw.labels) ? raw.labels : [],
    generatedAt: toIsoTime(raw.generated_at ?? raw.generatedAt ?? null),
  };
};

// whatsapp-calling-ownership.PASS.2 / PASS.9 — the pass JSON (full or the
// audience summary {id, status, from_user, to_user, mode}).
export const PASS_STATUS = Object.freeze({
  RINGING: 'ringing',
  CONSULTING: 'consulting',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  UNDONE: 'undone',
});

export const PASS_MODE = Object.freeze({
  TALK_FIRST: 'talk_first',
  PASS_NOW: 'pass_now',
});

// whatsapp-calling-ownership.PASS.2 — the note is ≤ 280 characters.
export const PASS_NOTE_MAX = 280;

export const isOpenPassStatus = status =>
  status === PASS_STATUS.RINGING || status === PASS_STATUS.CONSULTING;

export const normalizePass = (raw, callId = null) => {
  if (!raw?.id) return null;
  return {
    id: raw.id,
    callId: raw.call_id ?? raw.callId ?? callId,
    status: raw.status || PASS_STATUS.RINGING,
    mode: raw.mode || PASS_MODE.PASS_NOW,
    fromUser: normalizeUser(raw.from_user || raw.fromUser),
    toUser: normalizeUser(raw.to_user || raw.toUser),
    note: raw.note ?? null,
    assignConversation: raw.assign_conversation ?? null,
    automatic: !!raw.automatic,
    reason: raw.reason ?? null,
    expiresAt: toIsoTime(raw.expires_at ?? null),
    acceptedAt: toIsoTime(raw.accepted_at ?? null),
    completedAt: toIsoTime(raw.completed_at ?? null),
    undoUntil: toIsoTime(raw.undo_until ?? null),
  };
};

// Merge a later pass payload (often the summary) over what we know.
export const mergePass = (known, raw, callId = null) => {
  const next = normalizePass(raw, callId);
  if (!known) return next;
  if (!next) return known;
  const merged = { ...known };
  Object.entries(next).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (key === 'automatic' && !('automatic' in raw)) return;
    merged[key] = value;
  });
  return merged;
};

// whatsapp-calling-ownership.UI.3 / UI.4 / UI.8 — a pass note, quoted.
export const quoteNote = note => (note ? `\u201C${note}\u201D` : '');

// whatsapp-calling-ownership.PASS.7 / UI.8 — call legs.
// MessageList deep-camelCases content_attributes, so a voice_call message
// carries `callLegs`; the REST/cable payloads carry `call_legs`.
export const rawLegs = raw =>
  raw?.call_legs ?? raw?.callLegs ?? raw?.legs ?? null;

export const normalizeLegs = (legs = []) =>
  (Array.isArray(legs) ? legs : []).map(leg => ({
    id: leg.id,
    user: normalizeUser(leg.user),
    device: leg.device || null,
    joinedAt: toIsoTime(leg.joined_at ?? leg.joinedAt ?? null),
    leftAt: toIsoTime(leg.left_at ?? leg.leftAt ?? null),
    durationSeconds: leg.duration_seconds ?? leg.durationSeconds ?? null,
    passedBy: normalizeUser(leg.passed_by || leg.passedBy),
    note: leg.note || null,
    // whatsapp-calling-ai.AI_LEG.2 — "assistant" legs are the Studio Chat AI.
    kind: leg.kind || leg.user?.kind || 'agent',
    participantId: leg.participant_id ?? leg.participantId ?? null,
    // whatsapp-calling.WEB.3 timeline — Talk first / Pass now, when sent.
    passMode: leg.pass_mode ?? leg.passMode ?? null,
  }));

// whatsapp-calling.WEB.3 timeline — optional extras the call JSON may carry:
// who was rung per tier, and hold periods. Absent → the timeline skips them.
const normalizeRingTiers = tiers =>
  Array.isArray(tiers)
    ? tiers.map((tier, index) => ({
        tier: tier.tier ?? index + 1,
        users: (tier.users || []).map(normalizeUser).filter(Boolean),
        startedAt: toIsoTime(tier.started_at ?? tier.startedAt ?? null),
      }))
    : null;

const normalizeHolds = holds =>
  Array.isArray(holds)
    ? holds.map(hold => ({
        user: normalizeUser(hold.user),
        startedAt: toIsoTime(hold.started_at ?? hold.startedAt ?? null),
        endedAt: toIsoTime(hold.ended_at ?? hold.endedAt ?? null),
      }))
    : null;

// whatsapp-call-transcripts.REC.UI — the call's recording decision (made at
// call creation, kaption_calls.meta.recording): on/off, the notice mode, when
// the notice played and whether the agent stopped it.
// notice_played_at comes from the bridge's recording manifest, so it only
// arrives after the call; during the call the agent's "Play recording notice"
// sets notice_requested_at, and that is shown as "Notice played" until the
// real time replaces it.
export const normalizeCallRecording = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const requestedAt = toIsoTime(
    pick(raw.notice_requested_at, raw.noticeRequestedAt, null)
  );
  return {
    enabled: raw.enabled !== false && !raw.stopped_at && !raw.stoppedAt,
    noticeMode: pick(raw.notice_mode, raw.noticeMode, 'auto'),
    noticeRequestedAt: requestedAt,
    noticePlayedAt:
      toIsoTime(pick(raw.notice_played_at, raw.noticePlayedAt, null)) ||
      requestedAt,
    stoppedAt: toIsoTime(pick(raw.stopped_at, raw.stoppedAt, null)),
  };
};

const rawRecording = raw =>
  raw?.recording && typeof raw.recording === 'object'
    ? raw.recording
    : raw?.meta?.recording;

export const normalizeCall = (raw = {}) => {
  if (!raw) return null;
  const contact = raw.contact || {};
  const inbox = raw.inbox || {};
  const conversation = raw.conversation || {};
  const answeredBy = raw.answered_by || raw.answeredBy || null;
  return {
    id: raw.id,
    accountId: pick(raw.account_id, raw.accountId),
    conversationId: pick(
      raw.conversation_id,
      raw.conversationId,
      conversation.id,
      conversation.display_id
    ),
    contactId: pick(raw.contact_id, raw.contactId, contact.id),
    contactName: pick(
      raw.contact_name,
      raw.contactName,
      contact.name,
      contact.phone_number,
      ''
    ),
    contactAvatar: pick(
      raw.contact_avatar_url,
      raw.contact_avatar,
      raw.contactAvatar,
      contact.thumbnail,
      contact.avatar_url,
      ''
    ),
    contactPhone: pick(
      raw.contact_phone_number,
      raw.contact_phone,
      contact.phone_number,
      ''
    ),
    inboxId: pick(raw.inbox_id, raw.inboxId, inbox.id),
    inboxName: pick(raw.inbox_name, raw.inboxName, inbox.name, ''),
    direction: pick(raw.direction, CALL_DIRECTION.INBOUND),
    status: pick(raw.status, CALL_STATUS.RINGING),
    answeredBy: answeredBy
      ? {
          id: answeredBy.id,
          name: answeredBy.name || '',
          kind: answeredBy.kind || pick(raw.answered_by_kind, 'agent'),
        }
      : null,
    ringStartedAt: toIsoTime(
      pick(raw.ring_started_at, raw.started_at, raw.created_at, null)
    ),
    startedAt: toIsoTime(pick(raw.started_at, null)),
    answeredAt: toIsoTime(pick(raw.answered_at, null)),
    endedAt: toIsoTime(pick(raw.ended_at, null)),
    durationSeconds: pick(raw.duration_seconds, null),
    endReason: pick(raw.end_reason, null),
    recordingUrl: pick(raw.recording_url, raw.meta?.recording_url, null),
    // whatsapp-call-transcripts.EVENTS.3 — presenter fields.
    recording: normalizeCallRecording(rawRecording(raw)),
    recordingStatus: pick(raw.recording_status, raw.recordingStatus, null),
    transcriptStatus: pick(raw.transcript_status, raw.transcriptStatus, null),
    // whatsapp-calling-ownership.PASS.7 / PASS.9
    // Contract: `call_legs` (mobile + Rails); `legs` kept for older payloads.
    legs: rawLegs(raw) ? normalizeLegs(rawLegs(raw)) : null,
    pass: raw.pass ? normalizePass(raw.pass, raw.id) : null,
    ringTiers: normalizeRingTiers(raw.ring_tiers ?? raw.ringTiers),
    holds: normalizeHolds(raw.holds),
    // whatsapp-calling-ai.SUMMARY.2
    aiSummary: normalizeAiSummary(raw.ai || raw.data?.ai || raw.aiSummary),
  };
};

// Merge an EVENTS.3 payload ({id, status, duration_seconds, end_reason}) into
// a known call without letting it move backwards.
export const mergeCallUpdate = (call, update = {}) => {
  const next = { ...call };
  if (update.status && isForwardTransition(call.status, update.status)) {
    next.status = update.status;
  }
  if (update.duration_seconds !== undefined && update.duration_seconds !== null)
    next.durationSeconds = update.duration_seconds;
  if (update.end_reason) next.endReason = update.end_reason;
  if (update.answered_at) next.answeredAt = toIsoTime(update.answered_at);
  if (update.ended_at) next.endedAt = toIsoTime(update.ended_at);
  if (update.answered_by)
    next.answeredBy = {
      id: update.answered_by.id,
      name: update.answered_by.name || '',
      kind: update.answered_by.kind || 'agent',
    };
  // whatsapp-calling-ai.SUMMARY.2 — the bubble updates in place.
  const summary = normalizeAiSummary(update.ai);
  if (summary) next.aiSummary = summary;
  if (rawLegs(update)) next.legs = normalizeLegs(rawLegs(update));
  if (rawRecording(update))
    next.recording = normalizeCallRecording(rawRecording(update));
  if (update.transcript_status)
    next.transcriptStatus = update.transcript_status;
  if (update.recording_status) next.recordingStatus = update.recording_status;
  if (update.pass) next.pass = mergePass(call.pass, update.pass, call.id);
  return next;
};

export const formatCallDuration = totalSeconds => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

// Call log time: "14:05" today, "3 Oct" otherwise.
export const formatCallTime = (iso, now = new Date()) => {
  const parsed = iso ? new Date(iso) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  const sameDay = parsed.toDateString() === now.toDateString();
  return sameDay
    ? parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : parsed.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

// Status chip used by the call log (WEB.3): Ringing / Live / Answered /
// Missed / Waiting. Returns the i18n key suffix and the n-* token classes.
export const statusChip = status => {
  switch (status) {
    case CALL_STATUS.RINGING:
      return { key: 'RINGING', classes: 'bg-n-amber-3 text-n-amber-11' };
    case CALL_STATUS.ANSWERED:
      return { key: 'LIVE', classes: 'bg-n-teal-3 text-n-teal-11' };
    case CALL_STATUS.COMPLETED:
      return { key: 'ANSWERED', classes: 'bg-n-alpha-2 text-n-slate-11' };
    case CALL_STATUS.MISSED:
      return { key: 'MISSED', classes: 'bg-n-ruby-3 text-n-ruby-11' };
    case CALL_STATUS.DECLINED:
      return { key: 'DECLINED', classes: 'bg-n-ruby-3 text-n-ruby-11' };
    case CALL_STATUS.FAILED:
      return { key: 'FAILED', classes: 'bg-n-ruby-3 text-n-ruby-11' };
    case CALL_STATUS.PERMISSION_REQUESTED:
      return { key: 'WAITING', classes: 'bg-n-blue-3 text-n-blue-11' };
    default:
      return { key: 'ANSWERED', classes: 'bg-n-alpha-2 text-n-slate-11' };
  }
};

// The views of the Calls workspace (WEB.3) and the API.1 filter each maps to.
export const CALL_VIEWS = Object.freeze({
  all: {},
  missed: { status: CALL_STATUS.MISSED },
  waiting: { status: CALL_STATUS.PERMISSION_REQUESTED },
  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.UI.4 — calls the AI
  // took part in (tab shown only with KAPTION_CALL_ASSISTANT).
  ai: { answered_by_kind: 'assistant' },
});

// whatsapp-calling-ai.UI.4 — did the Studio Chat AI take part in the call?
export const isAiCall = call =>
  call?.answeredBy?.kind === 'assistant' ||
  (call?.legs || []).some(leg => leg.kind === 'assistant');

export const callMatchesView = (call, view) => {
  const filter = CALL_VIEWS[view] || {};
  if (filter.answered_by_kind === 'assistant') return isAiCall(call);
  if (!filter.status) return true;
  return call.status === filter.status;
};

// whatsapp-calling-ownership.LINE.3 — the Pass the line "Until" choice →
// the API value: until_taken_back, or unix seconds for the end of the
// agent's local day / now + 2 h (always within the 24 h limit).
export const resolveLineUntil = (value, now = new Date()) => {
  if (value === 'end_of_day') {
    const end = new Date(now);
    end.setHours(23, 59, 59, 0);
    return Math.floor(end.getTime() / 1000);
  }
  if (value === 'two_hours')
    return Math.floor(now.getTime() / 1000) + 2 * 60 * 60;
  return 'until_taken_back';
};
